import { useEffect } from 'react';
import { useSetAtom } from 'jotai';

import {
  cardSubscriptionsState,
  messagesState,
  pendingCouponRefreshesState,
  pendingRewardRefreshesState,
} from '../../../store';

import type { Card, CardSubscription, Message } from '@rewardopl/types';
import type { BaseDocument } from '@rewardopl/types/helpers/base';
import type { SetStateAction } from 'jotai';

type Action = 'append' | 'delete' | 'prepend' | 'update';

type BasePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: Omit<Action, 'delete'>;
  data: T;
};

type DeletePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'delete';
  data: Pick<T, '_id'>;
};

type RefreshPayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'refresh';
  data: T['_id'];
};

type Payload<T extends BaseDocument = BaseDocument> =
  | BasePayload<T>
  | DeletePayload<T>
  | RefreshPayload<T>;

function isDeletePayload<T extends BaseDocument>(payload: Payload<T>): payload is DeletePayload<T> {
  return payload.action === 'delete';
}

function isRefreshPayload<T extends BaseDocument>(
  payload: Payload<T>,
): payload is RefreshPayload<T> {
  return payload.action === 'refresh';
}

function handleAction<T extends BaseDocument>(
  setter: (nextValueOrSetter: SetStateAction<T[]>) => void,
  payload: Exclude<Payload<T>, RefreshPayload<T>>,
) {
  if (isDeletePayload(payload)) {
    const { data: nextData } = payload;

    setter((prevData) => prevData.filter((prevItem) => prevItem._id !== nextData._id));
    return;
  }

  const { action, data: nextData } = payload;

  function isSameDocument(prevItem: T) {
    return prevItem._id === nextData._id;
  }

  function replaceDocument(prevItem: T) {
    return isSameDocument(prevItem) ? nextData : prevItem;
  }

  switch (action) {
    case 'append':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [...prevData, nextData],
      );
      break;
    case 'prepend':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [nextData, ...prevData],
      );
      break;
    case 'update':
      setter((prevData) => prevData.map(replaceDocument));
      break;
    default:
    // Ignore unknown action types
  }
}

export default function WebsocketHandler() {
  const setCardSubscriptions = useSetAtom(cardSubscriptionsState);
  const setMessages = useSetAtom(messagesState);
  const setPendingCouponRefreshes = useSetAtom(pendingCouponRefreshesState);
  const setPendingRewardRefreshes = useSetAtom(pendingRewardRefreshesState);

  useEffect(() => {
    let ws: WebSocket;

    function setupWebSocket() {
      ws = new WebSocket(`wss://${window.location.host}/ws`);

      ws.addEventListener('message', (event) => {
        const { data: rawPayload } = event;

        const payload = JSON.parse(rawPayload) as Payload;

        if (isRefreshPayload(payload)) {
          switch (payload.type) {
            case 'coupon':
              setPendingCouponRefreshes((prev) => [
                ...prev,
                (payload as RefreshPayload<Card>).data,
              ]);
              break;
            case 'reward':
              setPendingRewardRefreshes((prev) => [
                ...prev,
                (payload as RefreshPayload<Card>).data,
              ]);
              break;
            default:
            // Ignore unknown refresh payload types
          }
        } else {
          switch (payload.type) {
            case 'card_subscription':
              handleAction(
                setCardSubscriptions,
                payload as Exclude<Payload<CardSubscription>, RefreshPayload<CardSubscription>>,
              );
              break;
            case 'message':
              handleAction(
                setMessages,
                payload as Exclude<Payload<Message>, RefreshPayload<Message>>,
              );
              break;
            default:
            // Ignore unknown payload types
          }
        }
      });

      ws.addEventListener('close', (event) => {
        if (!event.wasClean) {
          setTimeout(() => {
            setupWebSocket();
          }, 1000);
        }
      });
    }

    setupWebSocket();

    return () => {
      if (!ws) {
        return;
      }

      ws.close();
    };
  }, [setCardSubscriptions, setMessages, setPendingCouponRefreshes, setPendingRewardRefreshes]);

  return null;
}
